import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Chip, Stack } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleIcon from '@mui/icons-material/People';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useAuth from './useAuth.js';
import { useNotification } from "../../components/NotificationContext";
import axios from 'axios';
import { useAuth as useAuthContext } from '../../auth/AuthContext';

const boxData = [
  { name: 'Kategoriler', icon: CategoryIcon, routes: { add: '/admin/category/add', edit: '/admin/category/edit' } },
  { name: 'Urunler', icon: Inventory2Icon, routes: { add: '/admin/product/add', edit: '/admin/product/edit' } },
  { name: 'Siparisler', icon: ReceiptLongIcon, routes: { add: '/admin/orders' }, single: true, actionLabel: 'Goruntule' },
  { name: 'Kullanicilar', icon: PeopleIcon, routes: { add: '/admin/users' }, single: true, actionLabel: 'Goruntule' },
  { name: 'Urun Indirimleri', icon: LocalOfferIcon, routes: { add: '/admin/discount/add', edit: '/admin/discount/edit' } },
  { name: 'Sezon Urunleri', icon: EventAvailableIcon, routes: { add: '/admin/seasons' }, single: true, actionLabel: 'Duzenle' },
  { name: 'Kuponlar', icon: ConfirmationNumberIcon, routes: { add: '/admin/coupon/add', edit: '/admin/coupon/edit' } },
  { name: 'Raporlar', icon: AssessmentIcon, routes: { add: '/admin/reports' }, single: true, actionLabel: 'Goruntule' },
  { name: 'Kargo Ucretleri', icon: LocalShippingIcon, routes: { add: '/admin/shipping' }, single: true, actionLabel: 'Duzenle' },
];

const AdminPanel = () => {
  useAuth();
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const showNotification = useNotification();
  const token = localStorage.getItem('token');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem('token');
    logout();
    navigate('/');
    showNotification('Cikis yapildi!', 'success');
    localStorage.setItem('notificationSeverity', 'success');
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/status/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data.durum);
    } catch (err) {
      console.error('Durum verisi alinamadi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f7fb',
        px: { xs: 1.5, sm: 3, md: 4 },
        py: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#162033', fontSize: { xs: 28, sm: 34 } }}>
              Admin Panel
            </Typography>
            <Chip
              size="small"
              label={`Sistem: ${loading ? 'Yukleniyor' : status === 'aktif' ? 'Acik' : 'Kapali'}`}
              color={status === 'aktif' ? 'success' : 'error'}
              sx={{ mt: 1, fontWeight: 600, borderRadius: 1 }}
            />
          </Box>

          <Button
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
              borderRadius: 1,
              py: 1.1,
              px: 2,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Cikis Yap
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          {boxData.map((box) => {
            const Icon = box.icon;
            const actionIcon = box.actionLabel === 'Goruntule' ? <VisibilityIcon /> : <EditIcon />;

            return (
              <Box
                key={box.name}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #dce3ee',
                  borderRadius: 1,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  minHeight: 142,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 1,
                      bgcolor: '#e9f1ff',
                      color: '#1d4ed8',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#182235', fontSize: 18 }}>
                    {box.name}
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 'auto' }}>
                  {box.single ? (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={actionIcon}
                      onClick={() => navigate(box.routes.add)}
                      sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
                    >
                      {box.actionLabel || 'Ac'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(box.routes.add)}
                        sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
                      >
                        Ekle
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(box.routes.edit)}
                        sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
                      >
                        Duzenle
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPanel;
